import { apiClient } from './client';

export type EvidenceType =
  | 'anket' | 'foto' | 'ses' | 'video' | 'cizim' | 'metin' | 'yok';
export type ScoreBand = 'yetersiz' | 'yuzeysel' | 'iyi' | 'guclu' | '';
export type PersonaType =
  | 'egitimci' | 'burokrat' | 'vatandas' | 'isletmeci'
  | 'guvenlikci' | 'saglikci' | 'cevreci' | 'hukukcu' | 'uzman';

export const PERSONA_TYPE_LABELS: Record<PersonaType, { label: string; icon: string }> = {
  egitimci:    { label: 'Eğitimci',    icon: '🎓' },
  burokrat:    { label: 'Bürokrat',    icon: '🏛️' },
  vatandas:    { label: 'Vatandaş',    icon: '👥' },
  isletmeci:   { label: 'İşletmeci',   icon: '💼' },
  guvenlikci:  { label: 'Güvenlikçi',  icon: '🚓' },
  saglikci:    { label: 'Sağlıkçı',    icon: '⚕️' },
  cevreci:     { label: 'Çevreci',     icon: '🌳' },
  hukukcu:     { label: 'Hukukçu',     icon: '⚖️' },
  uzman:       { label: 'Uzman',       icon: '🔬' },
};

export interface PersonaPalette {
  fg: string;   // tam doygunluk (avatar text/icon, balon kenarı)
  bg: string;   // %15 opaklık (avatar daire arka planı, balon fill)
  ring: string; // hover/focus halkası
}

export const PERSONA_COLORS: Record<PersonaType, PersonaPalette> = {
  egitimci:   { fg: '#4f46e5', bg: '#eef2ff', ring: '#4f46e5' },
  burokrat:   { fg: '#1e40af', bg: '#dbeafe', ring: '#1e40af' },
  vatandas:   { fg: '#c2410c', bg: '#fed7aa', ring: '#c2410c' },
  isletmeci:  { fg: '#b45309', bg: '#fef3c7', ring: '#b45309' },
  guvenlikci: { fg: '#b91c1c', bg: '#fee2e2', ring: '#b91c1c' },
  saglikci:   { fg: '#0e7490', bg: '#cffafe', ring: '#0e7490' },
  cevreci:    { fg: '#15803d', bg: '#dcfce7', ring: '#15803d' },
  hukukcu:    { fg: '#7c3aed', bg: '#ede9fe', ring: '#7c3aed' },
  uzman:      { fg: '#c026d3', bg: '#fae8ff', ring: '#c026d3' },
};

export interface Project {
  id: number;
  problem_text: string;
  status: 'draft' | 'in_progress' | 'passed' | 'failed';
  created_at: string;
  finished_at: string | null;
  role_sessions: RoleSession[];
}

export interface RoleSession {
  id: number;
  role_name: string;
  role_focus: string;
  persona_type?: PersonaType;
  order: number;
  status: 'pending' | 'active' | 'passed' | 'failed';
  attempts_used: number;
  score: number | null;
  bonus: number;
  band?: ScoreBand;
  messages?: Message[];
}


export async function listProjects(): Promise<Project[]> {
  const res = await apiClient.get('/projects/');
  return Array.isArray(res.data) ? res.data : [];
}

export async function getProject(id: number): Promise<Project> {
  const res = await apiClient.get(`/projects/${id}/`);
  return res.data;
}

export async function createProject(
  problemText: string,
): Promise<Project> {
  const res = await apiClient.post('/projects/', {
    problem_text: problemText,
  });
  return res.data;
}

export interface Message {
  id: number;
  sender: 'student' | 'ai';
  content: string;
  attached_file: string | null;
  evidence_type?: EvidenceType;
  created_at: string;
}

export interface SendMessageResponse {
  message: Message;
  ai_response: Message;
  role_session: {
    status: 'pending' | 'active' | 'passed' | 'failed';
    attempts_used: number;
    score: number | null;
    bonus: number;
    band?: ScoreBand;
  };
}

export async function sendMessage(
  projectId: number,
  roleId: number,
  payload: { content?: string; file?: File; evidence_type?: EvidenceType },
): Promise<SendMessageResponse> {
  const formData = new FormData();
  if (payload.content) formData.append('content', payload.content);
  if (payload.file) formData.append('file', payload.file);
  if (payload.evidence_type) formData.append('evidence_type', payload.evidence_type);

  const res = await apiClient.post(
    `/projects/${projectId}/roles/${roleId}/send/`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  );
  return res.data;
}

export async function resetRoleSession(
  projectId: number,
  roleId: number,
): Promise<Project> {
  const res = await apiClient.post(`/projects/${projectId}/roles/${roleId}/reset/`);
  return res.data;
}

export async function deleteProject(projectId: number): Promise<{
  deleted: boolean;
  project: string;
  files_cleaned: number;
  message: string;
}> {
  const res = await apiClient.delete(`/projects/${projectId}/delete/`);
  return res.data;
}


// ---------------------------------------------------------------------------
// Diploma & grading
// ---------------------------------------------------------------------------

export interface DiplomaRoleSession {
  id: number;
  role_name: string;
  role_focus: string;
  persona_type: PersonaType | '';
  status: 'pending' | 'active' | 'passed' | 'failed';
  score: number | null;
  bonus: number;
  attempts_used: number;
}

export interface DiplomaData {
  project: {
    id: number;
    status: 'draft' | 'in_progress' | 'passed' | 'failed';
    problem_text: string;
    finished_at: string | null;
  };
  student: {
    id: number;
    display_name: string;
    classroom_name: string;
  };
  role_sessions: DiplomaRoleSession[];
  report: {
    role_scores: Record<string, number>;
    average_score: string;
    overall_status: 'passed' | 'failed';
  };
  diploma: {
    pdf_url: string | null;
    issued_at: string | null;
  };
}

export async function getDiploma(projectId: number): Promise<DiplomaData> {
  const res = await apiClient.get(`/grading/projects/${projectId}/`);
  return res.data;
}

export async function finalizeProject(projectId: number): Promise<{
  project: { id: number; status: string; finished_at: string };
  grade_report: {
    role_scores: Record<string, number>;
    average_score: string;
    diploma_pdf_url: string | null;
  };
}> {
  const res = await apiClient.post(`/grading/projects/${projectId}/finalize/`);
  return res.data;
}